import { Link } from '@tanstack/react-router';
import { useMemo, useState, type ReactNode } from 'react';

import type { PrescriptionRegistration } from '@/entities/business-relationship/api/businessRelationship.api';
import { usePrescriptionRegistrations } from '@/entities/business-relationship/hooks/useBusinessRelationships';
import { ErrorState } from '@/shared/components/StateViews';
import { Subtitle } from '@/shared/components/Subtitle';
import { Button } from '@/shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { Icon } from '@/shared/components/ui/icon';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import { cn } from '@/shared/lib/utils';
import { formatShortYm, toUrlYm } from '@/shared/utils/format';

/** 연도 선택 — 승인일을 모를 때 보여 줄 최소 연도 수 */
const MIN_YEARS = 2;

const yearOf = (date?: string | null) =>
  date ? Number(date.slice(0, 4)) : undefined;

/** 단계 하나 (Figma 거래처/상태(item)) — 위 24 높이 값 + 아래 13 Regular 이름 */
const Step = ({ top, label }: { top: ReactNode; label: string }) => (
  <div className="flex min-w-0 flex-1 flex-col items-center">
    <div className="flex h-6 items-center justify-center">{top}</div>
    <span className="text-body-13-r whitespace-nowrap text-fg-secondary">
      {label}
    </span>
  </div>
);

/** 처방확인 · 정산확정 — 완료 초록 / 미완료 회색 check-circle */
const DoneIcon = ({ done }: { done: boolean }) => (
  <Icon
    name="check-circle-solid-16"
    label={done ? '완료' : '미완료'}
    className={done ? 'text-green-500' : 'text-grey-300'}
  />
);

const StepArrow = () => (
  <Icon name="nav-arrow-right-line-16" className="text-grey-300" />
);

interface HistoryRow {
  key: string;
  /** 등록월 yyyyMM */
  ym: string;
  month: number;
  /** 없으면 그 달은 미등록 빈 행 */
  item?: PrescriptionRegistration;
  /** 같은 등록월에 정산월이 여러 개인 행 — 정산월을 함께 적는다 */
  multi: boolean;
}

/**
 * Figma 거래처_처방내역 (443:3212) — 한 달 한 줄 (높이 76 · 안쪽 16).
 * 월 | 처방등록 장수 › 처방확인 › 정산확정 | [상세 내역 ›]
 */
const HistoryRowItem = ({
  row,
  clinicId,
  isCurrent,
}: {
  row: HistoryRow;
  clinicId: number;
  isCurrent: boolean;
}) => {
  const { item } = row;
  const count = item?.imageCount ?? 0;
  return (
    <li className="flex min-h-[76px] items-center p-4">
      <div className="flex w-8 shrink-0 flex-col whitespace-nowrap">
        <span className="text-body-16-sb text-fg-secondary">{row.month}월</span>
        {row.multi && item && (
          <span className="text-caption-11-r text-fg-tertiary">
            {formatShortYm(item.settlementYearMonth)} 정산
          </span>
        )}
      </div>
      <div className="flex min-w-0 flex-1 items-center px-2">
        <Step
          label="처방등록"
          top={
            <span
              className={cn(
                'text-body-16-sb text-fg-primary',
                count === 0 &&
                  // 지금 등록할 달이 0장이면 빨강으로 재촉한다. 지난 빈 달은 흐리게
                  (isCurrent ? 'text-fg-error' : 'text-fg-light'),
              )}
            >
              {count}
            </span>
          }
        />
        <StepArrow />
        {/* 그 행 이미지 중 하나라도 확인 완료면 처방확인 완료 */}
        <Step
          label="처방확인"
          top={<DoneIcon done={(item?.verifiedImageCount ?? 0) > 0} />}
        />
        <StepArrow />
        <Step
          label="정산확정"
          top={<DoneIcon done={!!item?.isSettlementConfirmed} />}
        />
      </div>
      {item ? (
        <Button
          variant="text"
          size="small"
          className="text-fg-tertiary"
          asChild
        >
          <Link
            to={ROUTE_PATHS.PERFORMANCE_MONTH}
            params={{ yearMonth: toUrlYm(item.settlementYearMonth) }}
            search={{ clinicId }}
          >
            <span className="px-1">상세 내역</span>
            <Icon name="nav-arrow-right-line-16" />
          </Link>
        </Button>
      ) : (
        <Button
          variant="text"
          size="small"
          disabled
          rightIcon={<Icon name="nav-arrow-right-line-16" />}
        >
          상세 내역
        </Button>
      )}
    </li>
  );
};

/**
 * M-04 · M-05 처방 내역 (Figma 처방내역 495:6426) — 등록월별 처방등록 장수 · 처방확인 · 정산확정.
 * GET /prescription-registrations?registrationYear= 는 이미지가 있는 (등록월, 정산월)만 주므로
 * 빈 달은 미등록 행으로 채운다. 같은 등록월에 정산월이 여러 개면 행도 여러 개다 (합치지 않음).
 * v1.0: [전체 보기](12개월 펼치기)를 빼고 모든 달을 보여 준다. 처방 등록 진입점은 제목 줄 [처방 등록].
 *
 * @param currentRegistrationYm 오늘 올리면 들어갈 등록월 — 올해는 이 달까지 보여준다
 * @param latestApprovalDate 연도 선택 범위의 시작 (목록 카드 값)
 * @param endedAt 종료 · 거절 거래처면 종료일 — 기본 연도를 그 해로 둔다
 */
export const PrescriptionHistorySection = ({
  relationshipId,
  clinicId,
  currentRegistrationYm,
  latestApprovalDate,
  endedAt,
  highlightCurrent,
  onRegister,
}: {
  relationshipId: number;
  clinicId: number;
  currentRegistrationYm: string;
  latestApprovalDate: string | null;
  endedAt: string | null;
  /** 거래 중이면 이번 등록월 0장을 빨강으로 강조한다 */
  highlightCurrent: boolean;
  /** [처방 등록] — 등록 불가 상태 팝업 판정은 업로드 호스트가 한다 */
  onRegister: () => void;
}) => {
  const currentYear = Number(currentRegistrationYm.slice(0, 4));
  const currentMonth = Number(currentRegistrationYm.slice(4, 6));

  const years = useMemo(() => {
    const first = Math.min(
      yearOf(latestApprovalDate) ?? currentYear - (MIN_YEARS - 1),
      yearOf(endedAt) ?? currentYear,
      currentYear - (MIN_YEARS - 1),
    );
    return Array.from(
      { length: currentYear - first + 1 },
      (_, i) => currentYear - i,
    );
  }, [currentYear, latestApprovalDate, endedAt]);

  const [year, setYear] = useState(() => yearOf(endedAt) ?? currentYear);
  const registrations = usePrescriptionRegistrations(relationshipId, year);
  // 연도를 바꾸는 동안에는 이전 연도 응답이 placeholder로 남는다
  const items = useMemo(
    () =>
      registrations.data?.registrationYear === year
        ? registrations.data.items
        : [],
    [registrations.data, year],
  );

  const rows = useMemo(() => {
    const lastMonth = year === currentYear ? currentMonth : 12;
    return Array.from({ length: lastMonth }, (_, i) => {
      const month = lastMonth - i;
      const ym = `${year}${String(month).padStart(2, '0')}`;
      const matches = items.filter((r) => r.registrationYearMonth === ym);
      return matches.length === 0
        ? [{ key: ym, ym, month, multi: false }]
        : matches.map((item): HistoryRow => ({
            key: `${ym}-${item.settlementYearMonth}`,
            ym,
            month,
            item,
            multi: matches.length > 1,
          }));
    }).flat();
  }, [items, year, currentYear, currentMonth]);

  const loading = registrations.isPending || registrations.isPlaceholderData;

  return (
    <section className="flex flex-col gap-4 px-6">
      <Subtitle
        title="처방 내역"
        description="월별 처방 등록 · 처방 확인 · 정산 확정 진행을 볼 수 있어요."
        actions={<Button onClick={onRegister}>처방 등록</Button>}
      />
      <div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="grey"
              rightIcon={<Icon name="nav-arrow-down-line-24" />}
              aria-label={`연도 선택, ${year}년`}
            >
              {year}년
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {years.map((y) => (
              <DropdownMenuItem
                key={y}
                selected={y === year}
                onSelect={() => setYear(y)}
              >
                {y}년
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {registrations.isError ? (
        <ErrorState onRetry={() => void registrations.refetch()} />
      ) : loading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} className="h-[76px] rounded-20" />
          ))}
        </div>
      ) : (
        <ul className="flex flex-col overflow-hidden rounded-20">
          {rows.map((row) => (
            <HistoryRowItem
              key={row.key}
              row={row}
              clinicId={clinicId}
              // 등록할 수 없는 거래처(신청 · 종료 · 거절)는 이번 달 0장을 재촉하지 않는다
              isCurrent={highlightCurrent && row.ym === currentRegistrationYm}
            />
          ))}
        </ul>
      )}
    </section>
  );
};
